import React, { useEffect, useState } from "react";
import { Sun, Moon, Contrast } from "lucide-react";
import { motion } from "motion/react";

type Theme = "dark" | "light" | "white";

export default function ThemeSwitcher() {
  const [theme, setTheme] = useState<Theme>(() => {
    if (typeof window !== "undefined") {
      const saved = localStorage.getItem("theme");
      if (saved === "light" || saved === "dark" || saved === "white") {
        return saved as Theme;
      }
    }
    return "dark";
  });

  useEffect(() => {
    if (typeof window !== "undefined") {
      localStorage.setItem("theme", theme);
      document.documentElement.setAttribute("data-theme", theme);
    }
  }, [theme]);

  const themes: { id: Theme; icon: React.ReactNode; label: string }[] = [
    { id: "dark", icon: <Moon className="h-3.5 w-3.5" />, label: "Dark" },
    { id: "light", icon: <Sun className="h-3.5 w-3.5" />, label: "Light" },
    { id: "white", icon: <Contrast className="h-3.5 w-3.5" />, label: "White" },
  ];

  return (
    <div 
      id="theme-mode-switcher-container"
      className="relative flex items-center rounded-xl border border-slate-800 bg-slate-900/50 p-1"
    >
      {themes.map((t) => {
        const isActive = theme === t.id;
        return (
          <button
            key={t.id}
            id={`theme-btn-${t.id}`}
            onClick={() => setTheme(t.id)}
            title={`${t.label} Mode`}
            className={`relative flex h-8 items-center justify-center gap-1.5 rounded-lg px-2.5 text-xs font-semibold uppercase tracking-wider transition-colors focus:outline-none border-none cursor-pointer select-none ${
              isActive
                ? "text-slate-950 font-bold"
                : "text-slate-400 hover:text-slate-200"
            }`}
          >
            {isActive && (
              <motion.span
                layoutId="activeThemeHeader"
                className="absolute inset-0 rounded-lg bg-gradient-to-r from-amber-400 to-amber-500 shadow-[0_2px_10px_rgba(245,158,11,0.2)]"
                transition={{ type: "spring", stiffness: 400, damping: 30 }}
              />
            )}
            <span className="relative z-10 flex items-center gap-1">
              {t.icon}
              <span className="hidden md:inline text-[9px] font-bold tracking-tight">{t.label}</span>
            </span>
          </button>
        );
      })}
    </div>
  );
}
